/**
 * Iter-275.S — SOLE PARTNER ACCESS MODULE FOR CARSAVR.COM.
 *
 * Single entry point used by every affiliate CTA on the site —
 * homepage hero form, multi-step modal, comparison cards/tables,
 * calculator results, guide pages, state/city pages, sticky bars,
 * header, footer, admin "Test Partner Link". Nothing else in the
 * codebase is allowed to call `window.open` for partner navigation.
 *
 *   openPartner({
 *     partnerId,    // canonical id, e.g. "insurify" / "lendingtree"
 *     placement,    // surface, e.g. "homepage_form" / "rate_apply" / "marketplace_strip"
 *     url,          // resolved affiliate URL from admin Partner Routing registry
 *     metadata: { partnerName, category, quoteId, extraTracking },
 *     event,        // optional React synthetic event for preventDefault + stopPropagation
 *     suppressToast,// optional — disable built-in success/error toast
 *   })
 *
 * Iter-275.S — REMOVED the iter-275.Q nuclear `window.open` monkey-
 * patch. The patch was the root cause of a P0 regression where
 * affiliate links failed to open at all on real Chrome / mobile
 * browsers: `openPartner` set the placement+URL lock immediately
 * before calling `window.open`, the patched global `window.open`
 * then saw `state.until > now` and returned a fake stub object,
 * so the native popup was never actually opened. The internal
 * placement+URL lock inside `openPartner` is sufficient — it already
 * dedupes rapid double-clicks at the call site without any global
 * DOM/window interference. The build stamp lives on
 * `window.__CARSAVR_BUILD__` for production DevTools verification.
 */
import { toast } from "sonner";
import { recordClick } from "./api";
import { getAttribution } from "./attribution";
import { track, events } from "./analytics";

const PARTNER_LOCK_TIME_MS = 1500;
const PARTNER_LOCK_URL_MS = 3000;
const CARSAVR_BUILD = "iter-275.U";

// Diagnostic logging gated on NODE_ENV — gives engineers a paper trail
// in dev / staging without leaking to production users.
function _log(...args) {
  if (process.env.NODE_ENV !== "production") {
    console.log(...args);
  }
}

function _getLockState() {
  if (typeof window === "undefined") return null;
  if (!window.__CARSAVR_PARTNER_LOCK__) {
    window.__CARSAVR_PARTNER_LOCK__ = {
      until: 0,
      // Map<"placement::url", timestamp_ms> — placement-keyed dedupe so
      // legitimate cross-surface clicks on the same partner (e.g.
      // rate_apply then marketplace_strip) aren't unfairly blocked.
      entries: new Map(),
    };
  }
  return window.__CARSAVR_PARTNER_LOCK__;
}

function _isLocked(placement, url) {
  const state = _getLockState();
  if (!state) return false;
  const now = Date.now();
  if (now < state.until) return true;
  const key = `${placement || "default"}::${url}`;
  if (state.entries.has(key)) {
    const lastAt = state.entries.get(key);
    if (now - lastAt < PARTNER_LOCK_URL_MS) return true;
  }
  return false;
}

function _setLock(placement, url) {
  const state = _getLockState();
  if (!state) return;
  const now = Date.now();
  state.until = now + PARTNER_LOCK_TIME_MS;
  const key = `${placement || "default"}::${url}`;
  state.entries.set(key, now);
  if (state.entries.size > 32) {
    const cutoff = now - PARTNER_LOCK_URL_MS;
    for (const [k, v] of state.entries) {
      if (v < cutoff) state.entries.delete(k);
    }
  }
}

export function openPartner({
  partnerId = null,
  placement = "unknown",
  url,
  metadata = {},
  event = null,
  suppressToast = false,
} = {}) {
  // ① Block sibling browser handlers — fire BEFORE any side effect.
  if (event && typeof event === "object") {
    try { event.preventDefault?.(); } catch { /* ignore */ }
    try { event.stopPropagation?.(); } catch { /* ignore */ }
  }

  if (typeof window === "undefined") return false;

  // ② Strict URL validation — must be a parseable URL.
  let parsed = null;
  if (url && typeof url === "string") {
    try { parsed = new URL(url); } catch { parsed = null; }
  }
  if (!parsed) {
    _log("[openPartner] invalid URL", { partnerId, placement, url });
    if (!suppressToast) {
      try {
        toast.error(
          "Partner link is currently unavailable. Please try another option.",
        );
      } catch { /* ignore — toast host not mounted */ }
    }
    return false;
  }

  // ③ Optionally append ?ref=<quoteId> for postback reconciliation.
  let finalUrl = parsed.toString();
  const quoteId = metadata?.quoteId || null;
  if (quoteId) {
    try {
      parsed.searchParams.set("ref", quoteId);
      finalUrl = parsed.toString();
    } catch { /* fall through with current finalUrl */ }
  }

  // ④ Lock check BEFORE any side effect — duplicate clicks emit zero
  //   analytics, zero recordClick, zero window.open.
  if (_isLocked(placement, finalUrl)) {
    _log("[openPartner] duplicate suppressed", { partnerId, placement, finalUrl });
    return false;
  }

  // ⑤ Fire-and-forget analytics — never block the window.open call.
  try {
    // First-touch traffic source for this visit, so clicks can be split by search/referral/campaign.
    const attr = getAttribution() || {};
    recordClick({
      target: `partner:${partnerId || metadata?.partnerName || "unknown"}`,
      // Sliced to the /api/clicks field limits — an over-long value would 422 the whole click.
      referrer: attr.first_referrer ? String(attr.first_referrer).slice(0, 500) : null,
      utm_source: attr.utm_source ? String(attr.utm_source).slice(0, 80) : null,
      utm_medium: attr.utm_medium ? String(attr.utm_medium).slice(0, 80) : null,
      utm_campaign: attr.utm_campaign ? String(attr.utm_campaign).slice(0, 80) : null,
      landing_path: attr.first_landing_path ? String(attr.first_landing_path).slice(0, 200) : null,
      source_page:
        typeof window !== "undefined" ? window.location.pathname : "/",
      category: metadata?.category || null,
      partner_name: metadata?.partnerName || partnerId || null,
      placement,
      redirect_url: finalUrl,
      quote_id: quoteId,
    });
  } catch { /* ignore — recordClick is best-effort */ }

  try {
    track(events.AFFILIATE_CLICK, {
      partner_id: partnerId,
      partner_name: metadata?.partnerName || partnerId || null,
      offer_type: metadata?.category || null,
      page_location: placement,
      ...(metadata?.extraTracking || {}),
    });
  } catch { /* ignore — analytics is best-effort */ }

  // ⑥ SOLE window.open call in the entire codebase. SYNCHRONOUS — no
  //   await between the click handler and this line.
  //
  //   Iter-275.U — Cross-origin partner URLs require `noopener,noreferrer`
  //   for security (without it, the partner page gets `window.opener`
  //   and can navigate CarSavr away via `opener.location = …`). But per
  //   the HTML spec, when `noopener` is in the features list, modern
  //   browsers (Chrome / Brave / Safari / iOS / Android) ALWAYS return
  //   `null` from `window.open` — successful open or not. The previous
  //   `if (win && !win.closed)` success check therefore failed on every
  //   click and produced a false-positive red "popup blocked" toast,
  //   even though the partner tab opened correctly. The fix: treat a
  //   non-throwing synchronous `window.open` call as success. Only a
  //   true synchronous throw (embedded webviews that disallow
  //   `window.open` outright) reaches the manual-fallback toast below.
  let openedInNewTab = false;
  try {
    const win = window.open(finalUrl, "_blank", "noopener,noreferrer");
    // Some browsers DO return a non-null window even with noopener
    // (older Firefox, some webviews). When they do, scrub the opener
    // defensively. When they return null (the modern default), we
    // still treat the call as a success because the gesture was fresh
    // and synchronous.
    if (win && !win.closed) {
      try { win.opener = null; } catch { /* ignore */ }
    }
    openedInNewTab = true;
  } catch {
    // Synchronous throw — embedded webview that disallows window.open,
    // or a sandboxed iframe with no allow-popups. Fall through to the
    // manual-fallback toast.
  }

  // ⑦ Set lock AFTER a successful (non-throwing) open so a sync-throw
  //   first attempt doesn't lock the user out of clicking again.
  if (openedInNewTab) {
    _setLock(placement, finalUrl);
    _log("[openPartner] new tab opened", { partnerId, placement, finalUrl });
    if (!suppressToast) {
      try {
        const label = metadata?.partnerName
          ? `${metadata.partnerName} opened in a new tab. CarSavr will stay open so you can keep comparing.`
          : "Partner opened in a new tab. CarSavr will stay open so you can keep comparing.";
        toast.success(label, { duration: 5000 });
      } catch { /* ignore */ }
    }
    return true;
  }

  // ⑧ Synchronous throw fallback. Iter-275.U — this branch is now
  //   only reachable when the native `window.open` SYNCHRONOUSLY THREW
  //   (e.g. an embedded webview that disallows popups, or a sandboxed
  //   iframe without `allow-popups`). It is NOT reached when the
  //   browser merely returns `null` due to `noopener` — that's the
  //   modern default and is treated as a success above. Users on real
  //   Chrome / Safari / Firefox / mobile will essentially never see
  //   this toast.
  _log("[openPartner] window.open threw", { partnerId, placement, finalUrl });
  if (!suppressToast) {
    try {
      toast.error(
        "We couldn't open the partner automatically. Tap here to continue.",
        {
          duration: 8000,
          action: {
            label: "Open Partner",
            onClick: () => {
              if (typeof window !== "undefined") {
                // Open the partner in a NEW tab on explicit user click —
                // this `window.open` IS inside a fresh user gesture so
                // Chrome / Safari will allow it. Falls back to same-tab
                // only if even this explicit gesture is blocked.
                const w = window.open(finalUrl, "_blank", "noopener,noreferrer");
                if (!w) window.location.href = finalUrl;
              }
            },
          },
        },
      );
    } catch { /* ignore — toast host not available */ }
  }
  return false;
}

// Iter-275.P — Optional inspection helper for tests + diagnostic
// surfaces. Returns a snapshot of the current lock state without
// mutating anything.
export function _inspectPartnerLockState() {
  const state = _getLockState();
  if (!state) return null;
  return {
    until: state.until,
    entries: Array.from(state.entries.entries()),
  };
}

// ───────────────────────────────────────────────────────────────────
// Iter-275.S — Build stamp only.
//
// The iter-275.Q nuclear `window.open` monkey-patch was REMOVED in
// iter-275.S. The patch was breaking the affiliate flow on real
// Chrome / mobile browsers: `openPartner` set its placement-keyed
// lock immediately before calling `window.open`, the patched global
// `window.open` then saw `state.until > now`, treated the open as a
// duplicate, and returned a fake stub object — so the native popup
// was never actually opened. `openPartner`'s internal lock is
// sufficient on its own to dedupe rapid double-clicks at the call
// site (the placement+URL key catches micro-races without affecting
// legitimate first clicks).
//
// Only the build stamp survives so engineers can verify in DevTools
// (`console.log(window.__CARSAVR_BUILD__)`) which iteration is live.
// ───────────────────────────────────────────────────────────────────

function _installBuildStamp() {
  if (typeof window === "undefined") return;
  window.__CARSAVR_BUILD__ = CARSAVR_BUILD;
}

_installBuildStamp();
